import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Pagination from '../components/Pagination';

function BoardList() {
  const { boardId } = useParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [user, setUser] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [searchInfo, setSearchInfo] = useState(null);

  const boardNames = {
    'notice': '알림판',
    'bible_index': '성서마당',
    'theo_index': '신학마당',
    'current': '세상읽기',
    'free': '사랑채',
    'sermon': '설교모음',
    'mark': '매일묵상',
    'churchs': '교회소식',
    'member': '후원안내'
  };

  useEffect(() => {
    // 사용자 정보 확인
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser(payload);
      } catch (e) {
        console.error('Token parsing error:', e);
      }
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    const fetchBoardData = async () => {
      try {
        // 환경 감지: 로컬에서는 server.js, Vercel에서는 serverless function
        const apiBase = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
        let url;
        if (searchKeyword.trim()) {
          url = `${apiBase}/api/board/${boardId}/search?keyword=${encodeURIComponent(searchKeyword)}&searchType=${searchType}&page=${page}&limit=20`;
        } else {
          url = `${apiBase}/api/board/${boardId}?page=${page}&limit=20`;
        }

        const response = await fetch(url);
        const data = await response.json();

        // 응답 구조 확인
        const postsArray = data.posts || [];
        const paginationData = data.pagination;

        const formattedPosts = postsArray.map(item => {
          // XE 날짜 형식: 20040630210542 -> YYYY.MM.DD
          const dateStr = item.regdate || '';
          const year = dateStr.substring(0, 4);
          const month = dateStr.substring(4, 6);
          const day = dateStr.substring(6, 8);

          let isNew = false;
          if (dateStr.length >= 14) {
            const h = dateStr.substring(8, 10);
            const m = dateStr.substring(10, 12);
            const s = dateStr.substring(12, 14);
            const postDate = new Date(year, month - 1, day, h, m, s);
            const now = new Date();
            // 24시간 이내인지 확인
            const diffMs = now - postDate;
            const diffHours = diffMs / (1000 * 60 * 60);
            if (diffHours < 24) isNew = true;
          }

          // 수정된 글 체크 - 최근 3일(72시간) 이내에 수정된 경우에만 U마크 표시
          const lastUpdate = item.last_update || dateStr;
          let isModified = false;
          if (lastUpdate > dateStr && lastUpdate.length >= 14) {
            const uYear = parseInt(lastUpdate.substring(0, 4));
            const uMonth = parseInt(lastUpdate.substring(4, 6)) - 1;
            const uDay = parseInt(lastUpdate.substring(6, 8));
            const uHour = parseInt(lastUpdate.substring(8, 10) || 0);
            const uMinute = parseInt(lastUpdate.substring(10, 12) || 0);
            const uSecond = parseInt(lastUpdate.substring(12, 14) || 0);
            const updateDate = new Date(uYear, uMonth, uDay, uHour, uMinute, uSecond);
            const now = new Date();
            const diffHours = (now - updateDate) / (1000 * 60 * 60);
            isModified = diffHours < 72; // 72시간 = 3일
          }

          return {
            id: item.document_srl,
            title: item.title,
            author: item.nick_name || item.user_name,
            date: `${year}.${month}.${day}`,
            views: item.readed_count || 0,
            comments: item.comment_count || 0,
            isNew: isNew,
            isUpdated: isModified
          };
        });

        setPosts(formattedPosts);
        setPagination(paginationData);
        setSearchInfo(data.searchInfo || null);
      } catch (error) {
        console.error(`Board fetch error for ${boardId}:`, error);
        setPosts([]);
        setPagination(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBoardData();
  }, [boardId, page, searchKeyword, searchType]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo(0, 0);
  };

  const canWrite = () => {
    if (!user) return false;
    if (boardId === 'free') return true;
    return user.is_admin === 'Y' || user.nick_name === '정용섭';
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1); // Reset to first page when searching
  };

  const handleResetSearch = () => {
    setSearchKeyword('');
    setSearchType('all');
    setSearchInfo(null);
    setPage(1);
  };

  return (
    <div id="contentBody" style={{ display: 'block', padding: '30px 40px' }}>
      <div style={{ background: '#fff', borderRadius: '10px', padding: '30px', border: '1px solid #e8e8e8', position: 'relative' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#333', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #748320' }}>
          {boardNames[boardId] || boardId}
        </h2>

        {/* 검색 폼 */}
        <form onSubmit={handleSearch} style={{ marginBottom: '20px', padding: '15px', background: '#f9f9f9', borderRadius: '4px' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
            >
              <option value="all">전체</option>
              <option value="title">제목</option>
              <option value="content">내용</option>
              {boardId === 'sermon' && <option value="bible">성경본문</option>}
            </select>
            <input
              type="text"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              placeholder="검색어를 입력하세요"
              style={{ flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
            />
            <button
              type="submit"
              style={{ padding: '8px 20px', background: '#748320', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
            >
              검색
            </button>
            {searchKeyword.trim() && (
              <button
                type="button"
                onClick={handleResetSearch}
                style={{ padding: '8px 20px', background: '#666', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
              >
                초기화
              </button>
            )}
          </div>
        </form>

        {/* 검색 결과 정보 */}
        {searchInfo && (
          <div style={{ marginBottom: '15px', padding: '10px', background: '#e8f5e9', borderRadius: '4px', fontSize: '14px', color: '#2e7d32' }}>
            '<strong>{searchInfo.keyword}</strong>' 검색 결과: 총 <strong>{searchInfo.resultsCount}</strong>개의 게시글이 검색되었습니다.
          </div>
        )}

        {loading ? (
          <p style={{ padding: '40px', textAlign: 'center', color: '#999' }}>로딩 중...</p>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th style={{ width: '8%' }}>번호</th>
                  <th style={{ width: '51%' }}>제목</th>
                  <th style={{ width: '15%' }}>작성자</th>
                  <th style={{ width: '8%' }}>조회</th>
                  <th style={{ width: '18%' }}>날짜</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post, idx) => {
                  const postNumber = pagination
                    ? pagination.total - ((page - 1) * pagination.limit) - idx
                    : posts.length - idx;
                  return (
                    <tr key={idx}>
                      <td style={{ textAlign: 'center' }}>{postNumber}</td>
                      <td>
                        <Link to={`/board/${boardId}/${post.id}`}>{post.title}</Link>
                        {post.comments > 0 && (
                          <span style={{ color: '#748320', marginLeft: '5px' }}>
                            [{post.comments}]
                          </span>
                        )}
                        {post.isNew && (
                          <span className="badge-new" style={{
                            marginLeft: '5px',
                            fontSize: '10px',
                            color: '#fff',
                            backgroundColor: '#ff6b6b',
                            padding: '2px 5px',
                            borderRadius: '3px',
                            verticalAlign: 'middle',
                            fontWeight: 'bold',
                            lineHeight: '1'
                          }}>N</span>
                        )}
                        {post.isUpdated && (
                          <span className="badge-update" style={{
                            marginLeft: '5px',
                            fontSize: '10px',
                            color: '#fff',
                            backgroundColor: '#1890ff',
                            padding: '2px 5px',
                            borderRadius: '3px',
                            verticalAlign: 'middle',
                            fontWeight: 'bold',
                            lineHeight: '1'
                          }}>U</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>{post.author}</td>
                      <td style={{ textAlign: 'center' }}>{post.views || 0}</td>
                      <td style={{ textAlign: 'center' }}>{post.date}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {pagination && pagination.totalPages > 1 && (
              <Pagination
                current={pagination.current}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}

            {canWrite() && (
              <div style={{ textAlign: 'right', marginTop: '20px' }}>
                <Link
                  to={`/write/${boardId}`}
                  style={{
                    display: 'inline-block',
                    background: '#748320',
                    color: 'white',
                    textDecoration: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  글쓰기
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default BoardList;
